import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Bell, Phone, FileText, Calendar, CheckCircle2, Banknote, 
  Sparkles, Trash2, Clock, ArrowUpCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  rappel: { icon: Bell, color: 'text-violet-600 bg-violet-50' },
  dette: { icon: Banknote, color: 'text-green-600 bg-green-50' },
  facture: { icon: FileText, color: 'text-blue-600 bg-blue-50' },
  appel: { icon: Phone, color: 'text-amber-600 bg-amber-50' },
  rdv: { icon: Calendar, color: 'text-red-600 bg-red-50' },
  tache: { icon: CheckCircle2, color: 'text-gray-600 bg-gray-50' },
};

const PRIORITY_COLORS = {
  haute: 'border-l-red-500',
  moyenne: 'border-l-amber-500',
  basse: 'border-l-blue-500',
};

const TachesProactivesPage = () => {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('en_attente');
  const [loading, setLoading] = useState(true);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    const societeId = localStorage.getItem('societe_id') || localStorage.getItem('activeSocieteId');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    if (societeId) headers['X-Societe-Id'] = societeId;
    return headers;
  }, []);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter 
        ? `${API_URL}/api/taches-proactives?statut=${filter}`
        : `${API_URL}/api/taches-proactives`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.taches || []);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [filter, getHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/taches-proactives/stats`, { headers: getHeaders() });
      if (res.ok) setStats(await res.json());
    } catch (e) { /* silent */ }
  }, [getHeaders]);

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [fetchTasks, fetchStats]);

  const handleComplete = async (taskId) => {
    try {
      const res = await fetch(`${API_URL}/api/taches-proactives/${taskId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ statut: 'terminee' })
      });
      if (res.ok) {
        toast.success('Tache terminee');
        fetchTasks();
        fetchStats();
      }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleDelete = async (taskId) => {
    try {
      const res = await fetch(`${API_URL}/api/taches-proactives/${taskId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (res.ok) {
        toast.success('Tache supprimee');
        fetchTasks();
        fetchStats();
      }
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="proactive-tasks-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-violet-600" />
          Rappels IA
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Taches creees automatiquement par l'assistant depuis vos conversations
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-3" data-testid="tasks-stats">
          <Card 
            className={`text-center p-3 cursor-pointer transition-all ${filter === 'en_attente' ? 'ring-2 ring-violet-400' : ''}`}
            onClick={() => setFilter('en_attente')}
          >
            <p className="text-2xl font-bold text-amber-600">{stats.en_attente}</p>
            <p className="text-xs text-gray-500">En attente</p>
          </Card>
          <Card 
            className={`text-center p-3 cursor-pointer transition-all ${filter === 'en_cours' ? 'ring-2 ring-violet-400' : ''}`}
            onClick={() => setFilter('en_cours')}
          >
            <p className="text-2xl font-bold text-blue-600">{stats.en_cours}</p>
            <p className="text-xs text-gray-500">En cours</p>
          </Card>
          <Card 
            className={`text-center p-3 cursor-pointer transition-all ${filter === 'terminee' ? 'ring-2 ring-violet-400' : ''}`}
            onClick={() => setFilter('terminee')}
          >
            <p className="text-2xl font-bold text-green-600">{stats.terminee}</p>
            <p className="text-xs text-gray-500">Terminees</p>
          </Card>
        </div>
      )}

      {/* Tasks List */}
      <div className="space-y-3" data-testid="tasks-list">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Chargement...</div>
        ) : tasks.length === 0 ? (
          <Card className="text-center py-12">
            <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Aucune tache {filter ? `${filter.replace('_', ' ')}` : ''}</p>
            <p className="text-sm text-gray-400 mt-1">Dites a l'assistant : "Rappelle-moi de facturer Dupont"</p>
          </Card>
        ) : (
          tasks.map((task) => {
            const cfg = TYPE_CONFIG[task.type] || TYPE_CONFIG.tache;
            const Icon = cfg.icon;
            const borderColor = PRIORITY_COLORS[task.priorite] || PRIORITY_COLORS.moyenne;
            
            return (
              <Card key={task.id} className={`border-l-4 ${borderColor} hover:shadow-md transition-shadow`}>
                <CardContent className="p-4 flex items-start gap-4">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${cfg.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm">{task.titre}</p>
                      {task.priorite === 'haute' && (
                        <ArrowUpCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      )}
                    </div>
                    {task.description && (
                      <p className="text-xs text-gray-500 mt-1">{task.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      {task.personne_concernee && (
                        <span>{task.personne_concernee}</span>
                      )}
                      {task.montant && (
                        <span className="font-medium text-green-600">{task.montant} EUR</span>
                      )}
                      {task.date_echeance && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(task.date_echeance).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {task.statut !== 'terminee' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleComplete(task.id)}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        data-testid={`complete-task-${task.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(task.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-task-${task.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TachesProactivesPage;
